import { html, LitElement, nothing } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { LangLitTable } from '../../localization/lit-table-lang.js';
import { property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';
import { until } from 'lit/directives/until.js';
import { webmoduleTablePaginatorStyles, webmoduleTableStyles } from './table.styles.js';
import type { CSSResult, PropertyValues, TemplateResult } from 'lit';

/** Sentinel group key for rows that return no group key (rendered ungrouped). */
const UNGROUPED_KEY = '__wm_nogroup__';

type Snippet = string | TemplateResult;

/**
 * Resolves a title/snippet that may be a value, a function returning a value,
 * or a promise, into something Lit can render.
 */
function awaitableTemplate(
  template:
    | Snippet
    | Promise<Snippet>
    | HTMLElement
    | Promise<HTMLElement>
    | undefined
    | null
    | (() => Snippet)
    | (() => Promise<Snippet>),
  ...fallbacks: unknown[]
) {
  if (!template) return html``;
  const templateValue = typeof template === 'function' ? template() : template;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (templateValue as any)?.then ? until(templateValue as Promise<unknown>, ...fallbacks) : templateValue;
}

/**
 * Cell click handler. Return `true` if the click was fully handled (the table
 * will then stop propagation); return `false` to let the generic `cellclick`
 * event fire as well.
 */
export type WebModuleTableCellClickEvent = (
  e: Event,
  table: WebModuleTable,
  item: unknown,
  fieldName: string
) => boolean;

/**
 * Per-column cell fade options (see `<webmodule-fade>`). Set a column's `fade`
 * to `true` for the cell defaults, or to this shape to tune it.
 */
export interface WebModuleTableCellFade {
  /** Scroll axis. Default `'horizontal'` (one line that scrolls sideways). */
  orientation?: 'horizontal' | 'vertical';
  /** Remove the fade cue. Default `false` (keep it - the fade is the point). */
  withoutShadow?: boolean;
  /** Hide the scrollbar. Default `true` for a clean cell; drag/keyboard still scroll. */
  withoutScrollbar?: boolean;
  /** Ignore the mouse wheel so the page keeps scrolling. Default `true`. */
  withoutWheel?: boolean;
}

/**
 * Column definition for {@link WebModuleTable}.
 *
 * Sizing precedence: an explicit `width` pins the column; otherwise the column
 * shares remaining space equally with the other unpinned columns. `minWidth`
 * and `maxWidth` further constrain either case.
 */
export interface WebModuleTableColumnDef {
  /**
   * Optional stable id. Also namespaces this column's header slots:
   * `<id>-prefix`, `<id>-label`, `<id>-suffix`, `<id>-header-actions`.
   */
  id?: string;
  /** Header content (string/template). Optional if `label` or the label slot is used. */
  title?: string | TemplateResult | (() => string | TemplateResult);
  /**
   * Plain-text header label. Takes precedence over `title`. For HTML in the
   * header, use the `<id>-label` slot, or set `title` to a template.
   */
  label?: string | (() => string);
  /** Header content before the label (JS). The `<id>-prefix` slot overrides it. */
  headerPrefix?: string | TemplateResult | (() => string | TemplateResult);
  /** Header content after the label (JS). The `<id>-suffix` slot overrides it. */
  headerSuffix?: string | TemplateResult | (() => string | TemplateResult);
  /** Right-aligned header actions (JS). The `<id>-header-actions` slot overrides it. */
  headerActions?: string | TemplateResult | (() => string | TemplateResult);
  fieldName: string;
  /** Extra classes applied to the cell (header + body). */
  classes?: string;
  /** CSS width, e.g. '160px', '10rem'. Pins the column to this width. */
  width?: string;
  /** CSS min-width. */
  minWidth?: string;
  /** CSS max-width. */
  maxWidth?: string;
  /** Pin the column to the left or right edge (stays visible while scrolling). */
  fixed?: 'left' | 'right';
  /** Horizontal alignment of cell content. */
  align?: 'left' | 'center' | 'right';
  /**
   * Wrap this column's cell content in `<webmodule-fade>` so long values fade
   * and scroll instead of wrapping. `true` uses the cell defaults (horizontal,
   * scrollbar hidden, wheel left to the page - drag or keyboard to scroll);
   * pass {@link WebModuleTableCellFade} to tune. The table's `disableCellFade`
   * turns this off everywhere at once.
   */
  fade?: boolean | WebModuleTableCellFade;
  sortable?: boolean;
  isDefaultSortDirectionAscending?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  displayValue?: (table: WebModuleTable, item: any, index: number, fieldName: string) => TemplateResult | string;
  click?: WebModuleTableCellClickEvent;
  visible?: boolean;
  /** Grand-total footer cell renderer. */
  columnAggregate?: () => TemplateResult | string;
  /** Second (sub) grand-total footer row cell renderer. */
  columnSubAggregate?: () => TemplateResult | string;
  /**
   * Per-group footer cell renderer. Only used when grouping is active; a group
   * footer row is rendered for a group when at least one visible column defines
   * this callback.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  groupAggregate?: (groupItems: any[], groupValue: unknown) => TemplateResult | string;
}

/** Layout for an expanded row's content. */
export type WebModuleTableExtensionLayout = 'block' | 'columns';

/** Compound result for extension templates. */
export interface WebModuleTableExtensionResult {
  template: TemplateResult;
  /**
   * 'block' (default) wraps content in a padded panel. 'columns' renders the
   * content edge-to-edge so it can align with the parent columns (e.g. content
   * built with {@link WebModuleTable.renderChildRows}).
   */
  layout?: WebModuleTableExtensionLayout;
}

export type WebModuleTableRowExtensionTemplateEvent =
  | ((item: unknown) => TemplateResult)
  | ((item: unknown) => Promise<TemplateResult>)
  | ((item: unknown) => WebModuleTableExtensionResult)
  | ((item: unknown) => Promise<WebModuleTableExtensionResult>);

export type WebModuleTableRowExtensionTemplate =
  | TemplateResult
  | Promise<TemplateResult>
  | WebModuleTableExtensionResult
  | Promise<WebModuleTableExtensionResult>;

export type WebModuleTableExtensionTemplateProvider =
  | WebModuleTableRowExtensionTemplate
  | WebModuleTableRowExtensionTemplateEvent;

interface WebModuleTableExtension {
  itemKey: unknown;
  extensionProvider: WebModuleTableExtensionTemplateProvider;
}

/** Template function for custom group header content. */
export type WebModuleTableGroupHeaderTemplate = (
  groupValue: unknown,
  groupItems: object[],
  table: WebModuleTable
) => TemplateResult | string;

/**
 * Renders the group header's trailing action (e.g. a group "..." menu). It is pinned
 * to the right edge of the visible viewport - like a row's fixed-right column - so it
 * stays in view while the header scrolls horizontally.
 */
export type WebModuleTableGroupActionTemplate = (
  groupValue: unknown,
  groupItems: object[],
  table: WebModuleTable
) => TemplateResult | string;

export interface WebModuleTableItemMove {
  sourceIndex: number;
  targetIndex: number;
  /**
   * Set when the row was dropped onto a group header (grouping + drag active).
   * The consumer should move the item into this group - which usually means
   * also updating the item's group field so it stays contiguous with the group.
   */
  targetGroupKey?: string;
  /** Raw group value for {@link targetGroupKey} (e.g. the `groupBy` field value). */
  targetGroupValue?: unknown;
  /** Where in the group a group-header drop lands: 'start' (first) or 'end' (last). */
  dropPosition?: 'start' | 'end';
}

/**
 * A group that has no member rows but should still render a header (e.g. a group
 * a user emptied by dragging its items out, which must stay visible and remain a
 * drop target so items can be dragged back in). Native grouping derives groups
 * from row membership, so these are supplied separately via {@link WebModuleTable.emptyGroups}.
 */
export interface WebModuleTableEmptyGroup {
  /** Group key, matching what {@link WebModuleTable.getGroupByKey} would return for a member. */
  key: string;
  /** Group value passed to the header/action templates. */
  value: unknown;
  /**
   * Data-row index the header anchors before, so the empty group renders in its
   * natural position among the populated groups. Use the row count to render it
   * after all rows (a trailing group).
   */
  beforeIndex: number;
}

interface ColumnEntry {
  column: WebModuleTableColumnDef;
  index: number;
}

interface ColumnGroups {
  ordered: ColumnEntry[];
  left: ColumnEntry[];
  center: ColumnEntry[];
  right: ColumnEntry[];
  total: number;
}

interface RowGroup {
  value: unknown;
  key: string;
  items: object[];
  /** True for the run of rows whose group key was null/undefined: rendered as
   * plain rows with no group header or footer (partial grouping). */
  ungrouped: boolean;
}

/**
 * Paginator used by {@link WebModuleTable}. Rendered in its own shadow root so
 * it carries its own styling regardless of the host page.
 *
 * @event {{ index: Number }} page-change - Emitted when a page is selected.
 * @tag webmodule-table-paginator
 */
export class WebModuleTablePaginator extends LitElement {
  static styles = webmoduleTablePaginatorStyles;

  @property({ type: Number })
  index = 0;
  @property({ type: Number })
  count = 1;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('language-changed', this._onLanguageChanged);
  }

  disconnectedCallback() {
    window.removeEventListener('language-changed', this._onLanguageChanged);
    super.disconnectedCallback();
  }

  private _onLanguageChanged = () => this.requestUpdate();

  render() {
    let currentPage = this.index;
    if (typeof currentPage === 'string') currentPage = parseInt(currentPage);
    const pageCount = this.count;

    const visiblePages = 11;
    const pageDeductor = 5;
    let firstDisplayPage = currentPage - pageDeductor;
    if (firstDisplayPage < 0) firstDisplayPage = 0;
    let lastDisplayPage = firstDisplayPage + visiblePages;
    if (lastDisplayPage > pageCount) lastDisplayPage = pageCount;

    if (lastDisplayPage === pageCount && lastDisplayPage - firstDisplayPage + 1 < visiblePages)
      firstDisplayPage = Math.max(0, lastDisplayPage - visiblePages + 1);
    if (firstDisplayPage === 0 && lastDisplayPage - firstDisplayPage + 1 < visiblePages)
      lastDisplayPage = Math.min(pageCount, firstDisplayPage + visiblePages - 1);

    const includeSkipToLast = currentPage < pageCount - 1;

    const clickEvent = (e: Event) => {
      e.preventDefault();
      let newPage = parseInt((e.currentTarget as HTMLAnchorElement).getAttribute('page') ?? '0');
      newPage = Math.min(pageCount, Math.max(0, newPage));
      this.dispatchEvent(new CustomEvent('page-change', { detail: { index: newPage } }));
    };

    const pages = (): TemplateResult[] => {
      const pageResult: TemplateResult[] = [];
      for (let i = firstDisplayPage; i < lastDisplayPage; i++) {
        if (i === currentPage) {
          pageResult.push(html`<li class="page-item active"><span class="page-link">${i + 1}</span></li>`);
        } else {
          pageResult.push(
            html`<li class="page-item">
              <a class="page-link" href="#" page="${i}" @click=${clickEvent}>${i + 1}</a>
            </li>`
          );
        }
      }
      return pageResult;
    };

    const currentPageClass = `page-item ${currentPage === 0 ? 'disabled' : ''}`;
    const lastPageClass = `page-item ${currentPage === pageCount - 1 ? 'disabled' : ''}`;
    const skipToLastClass = `page-item ${!includeSkipToLast ? 'disabled' : ''}`;
    return html`<nav aria-label="Page navigation">
      <ul class="pagination">
        <li class="page-item ${currentPage === 0 ? 'disabled' : ''}">
          <a class="page-link" href="#" page="0" @click=${clickEvent}>${LangLitTable.paginatorFirst()}</a>
        </li>
        <li class=${currentPageClass}>
          <a class="page-link" href="#" page="${currentPage - 1}" rel="prev" @click=${clickEvent}
            >${LangLitTable.paginatorPrev()}</a
          >
        </li>
        ${pages()}
        <li class=${lastPageClass}>
          <a class="page-link" href="#" page="${currentPage + 1}" rel="next" @click=${clickEvent}
            >${LangLitTable.paginatorNext()}</a
          >
        </li>
        <li class=${skipToLastClass}>
          <a class="page-link" href="#" page="${pageCount - 1}" @click=${clickEvent}>${LangLitTable.paginatorLast()}</a>
        </li>
      </ul>
    </nav>`;
  }
}

/**
 * A feature-rich data table for the DealerUI site. Successor to
 * {@link WebModuleLitTable} (`<webmodule-lit-table>`); the original is kept
 * intact so tables can be migrated one at a time.
 *
 * Capabilities over the original:
 * - Sticky header and sticky (pinned left/right) columns
 * - Row grouping with per-group headers (collapsible) and per-group footers
 * - Expandable rows revealing a free HTML block or a column-aligned sub-grid
 * - Drag & drop row reordering (mouse + touch)
 * - Per-column width / min / max / alignment
 *
 * Data loading mirrors the original table: set `.data` directly, or react to
 * the `fetch` event for server-side paging.
 *
 * @event fetch - Emitted requesting a page of data (server paging).
 * @event {{ table: WebModuleTable, item: Object }} rowclick - Emitted when a row is clicked.
 * @event {{ table: WebModuleTable, selectedkeys: Object }} rowselect - Emitted when selection changes.
 * @event {{ table: WebModuleTable, item: Object, colDef: WebModuleTableColumnDef, originalEvent: Event }} cellclick - Emitted when a cell is clicked.
 * @event {{ table: WebModuleTable, item: Object, value: unknown, key: string }} grouptoggle - Emitted when a group is collapsed/expanded.
 * @event {WebModuleTableItemMove} webmodule-table-item-move - Emitted when rows are reordered via drag/drop.
 * @event {{ table: WebModuleTable, pageLength: number }} page-size-change - Emitted when the page-size selector changes.
 *
 * Header regions (prefix/label/suffix/header-actions) can be configured in JS via
 * the column def (`headerPrefix`, `label`/`title`, `headerSuffix`, `headerActions`)
 * or in HTML via the slots below (which override the JS value when filled).
 *
 * @slot {id}-prefix - Content before a column's header label (column needs an `id`).
 * @slot {id}-label - Replaces a column's header label with HTML (column needs an `id`).
 * @slot {id}-suffix - Content after a column's header label (column needs an `id`).
 * @slot {id}-header-actions - Right-aligned header actions; works best with `<webmodule-icon-button>` (column needs an `id`).
 *
 * @tag webmodule-table
 */
export class WebModuleTable extends LitElement {
  static styles = [webmoduleTableStyles];

  /**
   * Inline grip glyph for the drag handle. Inlined (rather than a
   * `<webmodule-icon>`) so it never depends on an icon library being
   * registered in the host app.
   */
  private static readonly gripIcon = html`<svg
    viewBox="0 0 16 16"
    width="14"
    height="14"
    fill="currentColor"
    aria-hidden="true"
  >
    <circle cx="5" cy="3" r="1.4" />
    <circle cx="11" cy="3" r="1.4" />
    <circle cx="5" cy="8" r="1.4" />
    <circle cx="11" cy="8" r="1.4" />
    <circle cx="5" cy="13" r="1.4" />
    <circle cx="11" cy="13" r="1.4" />
  </svg>`;

  private dragSrcEl: HTMLElement | null = null;
  private lastDropTarget: HTMLElement | null = null;
  private draggedIndex: number | null = null;
  private noDropClass = 'no-drop-allowed';
  /** Lookup of the current render's groups by key, for resolving group drops. */
  private _groupsByKey = new Map<string, RowGroup>();
  /** Constructed stylesheet holding the consumer's {@link customStyles}, if any. */
  private _customSheet?: CSSStyleSheet;

  @property({ type: Boolean })
  enableDrag = false;

  /**
   * Where a row dropped onto a group header lands within that group:
   * 'end' (default, appended) or 'start' (inserted first). Emitted back on the
   * move event as `dropPosition` together with the target group.
   */
  @property({ type: String })
  groupDropPosition: 'start' | 'end' = 'end';

  /**
   * Optional. Groups with no member rows to render as headers anyway, so an
   * emptied group stays visible and remains a drop target. Only consulted when
   * the table is grouped; each entry is anchored via its `beforeIndex`.
   */
  @property({ attribute: false })
  emptyGroups: WebModuleTableEmptyGroup[] = [];

  @property({ type: Boolean })
  clickrows = false;
  /** none | single | multi */
  @property({ type: String })
  selectmode = 'none';
  /** standardtable | nestedtable | halftable | '' */
  @property({ type: String })
  tablestyle = 'standardtable';

  /**
   * Drop shadow around the table container. Default `true` (standalone look);
   * set `false` for a flat table that sits inside an existing page surface.
   */
  @property({ type: Boolean, reflect: true })
  elevated = true;

  /**
   * Vertical (left/right) borders between cells. Default `true`. When `false`,
   * only the horizontal row dividers remain (the row top/bottom borders are
   * always shown).
   */
  @property({ type: Boolean, reflect: true })
  cellBorders = true;

  /**
   * Uppercase the header label text. Default `true`. When `false`, the header
   * renders the provided text as-is.
   */
  @property({ type: Boolean, reflect: true })
  uppercaseHeader = true;

  /**
   * Pin the grand-total footer (the column aggregate / sub-aggregate "totals"
   * row) to the bottom of the scroll viewport so it stays visible while the body
   * scrolls. Default `true`. When `false` the footer flows at the natural end of
   * the table content and scrolls away with the rows (the flat-table look used by
   * {@link DataTableWrapper}). Per-group footers are always inline and unaffected.
   */
  @property({ type: Boolean, reflect: true })
  stickyFooter = true;

  /**
   * Master off switch for per-column cell fading. Default `false`. When `true`,
   * no column is wrapped in `<webmodule-fade>` regardless of its `fade` flag, so
   * a whole table can drop back to plain wrapping cells in one place.
   */
  @property({ attribute: 'disable-cell-fade', type: Boolean, reflect: true })
  disableCellFade = false;

  /**
   * Extra CSS injected into the table's shadow root. Because cell, group-header
   * and row-extension content is rendered inside the shadow DOM, host-page CSS
   * cannot reach it; a consumer that renders rich content (custom classes,
   * column-specific styling) passes that styling here so it applies. Accepts a
   * raw CSS string or a Lit `css` result. Applied via a constructed stylesheet,
   * so it re-applies whenever the value changes.
   */
  @property({ attribute: false })
  customStyles?: string | CSSResult;

  @property({ type: Number })
  rowCount = 0;
  @property({ type: Number })
  pageLength = 3000;

  /** Optional cap on the body scroll height, e.g. '60vh', '400px'. */
  @property({ type: String })
  bodyMaxHeight?: string;
  /** Optional minimum table width to force horizontal scroll, e.g. '1200px'. */
  @property({ type: String })
  minTableWidth?: string;
  /** Message shown when there are no rows. */
  @property({ type: String })
  emptyMessage = '';

  /** Horizontal alignment of the paginator within its row. */
  @property({ type: String })
  paginatorAlign: 'left' | 'middle' | 'right' = 'middle';

  /**
   * Show a "showing X–Y of N / page n of m" summary alongside the paginator.
   * Most useful for server paging where `rowCount` reflects the full result.
   */
  @property({ type: Boolean })
  showPaginationInfo = false;

  /**
   * Page-size choices. When non-empty, a "rows per page" selector is rendered.
   * Choosing a size sets `pageLength`, resets to the first page, emits
   * `page-size-change`, and re-fetches.
   */
  @property({ type: Array })
  pageSizeOptions: number[] = [];

  /** Label shown before the page-size selector. */
  @property({ type: String })
  pageSizeLabel = 'Rows per page';

  /** Optional custom formatter for the pagination summary text. */
  @property({ attribute: false })
  paginationInfoFormatter?: (info: {
    start: number;
    end: number;
    total: number;
    page: number;
    pageCount: number;
  }) => string;

  @property({ attribute: false })
  keyevent?: (item: object) => unknown;
  @property({ attribute: false })
  rowidevent?: (item: object) => string | null;
  @property({ attribute: false })
  itemRowClassEvent?: (item: object) => string;
  /**
   * Returns a semantic status variant for a row - `'danger' | 'warning' |
   * 'success' | 'info' | 'primary'` - applied as a `wm-table-row--<variant>` class
   * that the table styles itself (a tinted row background, including the sticky
   * segments, plus a matching hover). `'primary'` tints with the theme primary
   * colour. Return a falsy value for the default appearance. Prefer this over
   * routing row colours through {@link customStyles}.
   */
  @property({ attribute: false })
  rowVariantEvent?: (item: object) => string | null | undefined;
  @property({ attribute: false })
  checkEnabledDragEvent?: (item: object) => boolean;

  /** Field name to group rows by. Data should already be ordered by this field. */
  @property({ type: String })
  groupBy?: string;
  /**
   * Returns a group key for an item. Takes precedence over {@link groupBy}.
   * The returned string both identifies and orders groups.
   */
  @property({ attribute: false })
  getGroupByKey?: (item: object) => string | null;
  /** Custom renderer for group header content. */
  @property({ attribute: false })
  groupHeaderTemplate?: WebModuleTableGroupHeaderTemplate;
  /**
   * Optional group-header action, pinned to the right edge of the viewport (like a
   * row's fixed-right column) so it stays visible while the header scrolls sideways.
   */
  @property({ attribute: false })
  groupActionTemplate?: WebModuleTableGroupActionTemplate;
  /** Allow groups to be collapsed by clicking the group header. */
  @property({ type: Boolean })
  collapsibleGroups = false;

  @property({ type: String })
  sortField?: string;
  @property({ type: Boolean })
  sortAscending?: boolean;

  /**
   * Controls which rows render:
   * - 'all' (default), 'selected', or 'unselected'.
   */
  @property({ type: String })
  visibleMode: 'selected' | 'unselected' | 'all' = 'all';

  private _selectedKeys: unknown[] = [];
  // Membership set mirroring _selectedKeys for O(1) "is this row selected" checks.
  // rowTemplate() and getPageData() test membership once per row per render, so a
  // Set avoids a per-row linear indexOf scan over the selection.
  private _selectedKeySet = new Set<unknown>();

  @property({ attribute: false })
  set selectedKeys(value: unknown[]) {
    const old = this._selectedKeys;
    this._selectedKeys = value ?? [];
    this._selectedKeySet = new Set(this._selectedKeys);
    this.requestUpdate('selectedKeys', old);
  }

  get selectedKeys(): unknown[] {
    return this._selectedKeys;
  }

  @state()
  data: object[] = [];
  @state()
  private _extensions: WebModuleTableExtension[] = [];
  // Primary-key -> extension index over _extensions, kept in sync by setExtensions.
  // isExpanded()/extensionTemplate() are called once per money column per row (~11x
  // per quote-item row), so an O(1) lookup here avoids an O(rows x cols x expanded)
  // linear scan on every render.
  private _extensionByKey = new Map<unknown, WebModuleTableExtension>();
  @state()
  private _collapsedGroups = new Set<string>();

  private _firstConnected = false;

  private _columns: WebModuleTableColumnDef[] = [];

  @property({ type: Array })
  set columns(value: WebModuleTableColumnDef[]) {
    const next = value ?? [];
    this.validateFixedColumns(next);
    const old = this._columns;
    this._columns = next;
    this.requestUpdate('columns', old);
  }

  get columns(): WebModuleTableColumnDef[] {
    if (!this.enableDrag) return this._columns;
    const handle: WebModuleTableColumnDef = {
      title: '',
      fieldName: '__drag',
      classes: 'drag-grab-handle',
      width: '2rem',
      fixed: 'left',
      align: 'center',
      displayValue: (_t, item: object) =>
        this.enableDragOnRow(item)
          ? html`<span
              class="draggable-item"
              aria-label="Drag to reorder"
              draggable="true"
              @dragstart=${this.handleDragStart}
              @dragend=${this.handleDragEnd}
              @touchstart=${this.dragtouchstart}
              @touchmove=${this.dragtouchmove}
              @touchend=${this.dragtouchend}
              @touchcancel=${this.handleTouchCancel}
              >${WebModuleTable.gripIcon}</span
            >`
          : html``
    };
    return [handle, ...this._columns];
  }

  @property({ type: Number })
  private _pageIndex = 0;

  public get pageIndex(): number {
    return this._pageIndex;
  }

  public set pageIndex(value: number) {
    this._pageIndex = this.pageNumberCheck(value);
    this.fetchEvent();
  }

  protected get isSortingAscending(): boolean {
    return this.sortAscending === undefined || this.sortAscending;
  }

  protected get pageCount(): number {
    return Math.ceil(this.rowCount / this.pageLength);
  }

  protected get isSelectable(): boolean {
    return this.selectmode !== 'none';
  }

  protected get isGrouped(): boolean {
    return !!this.groupBy || !!this.getGroupByKey;
  }

  connectedCallback(): void {
    if (!this._firstConnected) this.configure();
    this._firstConnected = true;
    window.addEventListener('language-changed', this._onLanguageChanged);
    super.connectedCallback();
  }

  disconnectedCallback(): void {
    window.removeEventListener('language-changed', this._onLanguageChanged);
    super.disconnectedCallback();
  }

  private _onLanguageChanged = () => this.requestUpdate();

  /** Override to set columns/sort defaults before first render. */
  configure() {
    // override
  }

  protected willUpdate(changed: PropertyValues): void {
    super.willUpdate(changed);
    // Invalidate the per-render visible-column-groups memo before each render.
    this._columnGroupsCache = null;
  }

  protected updated(changed: PropertyValues): void {
    if (changed.has('customStyles')) this.applyCustomStyles();
  }

  /**
   * Sync the consumer's {@link customStyles} into a constructed stylesheet on
   * the shadow root, replacing any previously applied one. Kept separate from
   * the component's `static styles` so the consumer sheet can change at runtime.
   */
  private applyCustomStyles() {
    const root = this.renderRoot as ShadowRoot | undefined;
    if (!root || !('adoptedStyleSheets' in root)) return;
    if (this._customSheet) {
      root.adoptedStyleSheets = root.adoptedStyleSheets.filter(sheet => sheet !== this._customSheet);
      this._customSheet = undefined;
    }
    const raw = this.customStyles;
    const text = (typeof raw === 'string' ? raw : raw?.cssText)?.trim();
    if (!text) return;
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(text);
    this._customSheet = sheet;
    root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
  }

  private validateFixedColumns(columns: WebModuleTableColumnDef[]) {
    type Phase = 'left' | 'middle' | 'right';
    let phase: Phase = 'left';
    columns.forEach(column => {
      if (column.fixed === 'left') {
        if (phase !== 'left') throw new Error('Fixed-left columns must be contiguous at the start of the column set.');
        return;
      }
      if (column.fixed === 'right') {
        phase = 'right';
        return;
      }
      if (phase === 'right')
        throw new Error(
          'Non-fixed columns cannot appear after fixed-right columns. Move fixed-right columns to the end.'
        );
      phase = 'middle';
    });
  }

  // ---- Data lifecycle (mirrors the original table's fetch-event model) ----

  protected pageNumberCheck(value: number): number {
    return value;
  }

  protected async firstUpdated() {
    const data = await this.prepareData();
    if (data) this.data = data;
    else this.fetchEvent();
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  protected async prepareData(): Promise<object[] | null> {
    return null;
  }

  fetchEvent() {
    this.dispatchCustom('fetch', {
      table: this,
      pageIndex: this.pageIndex,
      pageLength: this.pageLength,
      sortField: this.sortField,
      sortAscending: this.sortAscending
    });
  }

  // ---- Events ----

  rowClickEvent(item: object) {
    this.dispatchCustom('rowclick', { table: this, item });
  }

  rowSelectEvent() {
    this.dispatchCustom('rowselect', { table: this, selectedkeys: this.selectedKeys });
  }

  cellClickEvent(originalEvent: Event, item: object, colDef: WebModuleTableColumnDef) {
    this.dispatchCustom('cellclick', { table: this, item, colDef, originalEvent });
  }

  afterRowTemplate(item: object, index: number) {
    const event = { table: this, item, index, template: undefined };
    this.dispatchCustom('after-row-render', event);
    return event.template;
  }

  beforeRowTemplate(item: object, index: number) {
    const event = { table: this, item, index, template: undefined };
    this.dispatchCustom('before-row-render', event);
    return event.template;
  }

  // ---- Keys / per-row hooks ----

  getPrimaryKey(item: object): unknown {
    if (this.keyevent) return this.keyevent(item);
    return item;
  }

  getItemRowClass(item: object): string {
    return this.itemRowClassEvent?.(item) ?? '';
  }

  getRowId(item: object): string | null {
    return this.rowidevent ? this.rowidevent(item) : null;
  }

  enableDragOnRow(item: object) {
    if (!this.enableDrag) return false;
    if (this.checkEnabledDragEvent) return this.checkEnabledDragEvent(item);
    return true;
  }

  // ---- Expanders (extensions) ----

  isExpanded(item: object) {
    return this._extensionByKey.get(this.getPrimaryKey(item));
  }

  addExtension(item: object, template: WebModuleTableExtensionTemplateProvider) {
    const itemKey = this.getPrimaryKey(item);
    this.setExtensions([
      ...this._extensions.filter(x => x.itemKey !== itemKey),
      { itemKey, extensionProvider: template }
    ]);
  }

  remExtension(item: object) {
    const itemKey = this.getPrimaryKey(item);
    this.setExtensions(this._extensions.filter(x => x.itemKey !== itemKey));
  }

  /** Toggle an expander. The provider is only consulted when expanding. */
  toggleExtension(item: object, template: WebModuleTableExtensionTemplateProvider) {
    if (this.isExpanded(item)) this.remExtension(item);
    else this.addExtension(item, template);
  }

  /** True when every currently rendered row has an expander open. */
  areAllExpanded(): boolean {
    const data = this.getPageData();
    if (!data.length) return false;
    return data.every(item => !!this.isExpanded(item));
  }

  /** Expand every currently rendered row using `template` as the provider. */
  expandAll(template: WebModuleTableExtensionTemplateProvider) {
    this.setExtensions(
      this.getPageData().map(item => ({ itemKey: this.getPrimaryKey(item), extensionProvider: template }))
    );
  }

  /** Collapse all open expanders. */
  collapseAll() {
    if (!this._extensions.length) return;
    this.setExtensions([]);
  }

  /**
   * Flip every row's expander: collapse all if all are already open, otherwise
   * expand all using `template`.
   */
  toggleAll(template: WebModuleTableExtensionTemplateProvider) {
    if (this.areAllExpanded()) this.collapseAll();
    else this.expandAll(template);
  }

  /** Replaces the extension set while preserving the body scroll position. */
  private setExtensions(next: WebModuleTableExtension[]) {
    const scroll = this.scrollContainer;
    const scrollTop = scroll?.scrollTop ?? 0;
    const scrollLeft = scroll?.scrollLeft ?? 0;
    this._extensions = next;
    this._extensionByKey = new Map(next.map(x => [x.itemKey, x]));
    void this.updateComplete.then(() => {
      if (scroll) {
        scroll.scrollTop = scrollTop;
        scroll.scrollLeft = scrollLeft;
      }
    });
  }

  private get scrollContainer(): HTMLElement | null {
    return this.renderRoot.querySelector<HTMLElement>('.wm-table-scroll');
  }

  // ---- Pagination summary + page size ----

  protected paginationInfoText(): string {
    const total = this.rowCount;
    const pageCount = Math.max(1, this.pageCount);
    const page = this.pageIndex + 1;
    const start = total === 0 ? 0 : this.pageIndex * this.pageLength + 1;
    const end = Math.min((this.pageIndex + 1) * this.pageLength, total);
    if (this.paginationInfoFormatter) return this.paginationInfoFormatter({ start, end, total, page, pageCount });
    return `Showing ${start}–${end} of ${total} · Page ${page} of ${pageCount}`;
  }

  private pageSizeTemplate(): TemplateResult {
    const change = (e: Event) => {
      const size = Number((e.currentTarget as HTMLSelectElement).value);
      if (!size || size === this.pageLength) return;
      this.pageLength = size;
      this.dispatchCustom('page-size-change', { table: this, pageLength: size });
      this.pageIndex = 0; // resets to first page and triggers fetch
    };
    return html`<label class="wm-table-pagesize">
      <span class="wm-table-pagesize-label">${this.pageSizeLabel}</span>
      <select class="wm-table-pagesize-select" @change=${change}>
        ${this.pageSizeOptions.map(n => html`<option value=${n} ?selected=${n === this.pageLength}>${n}</option>`)}
      </select>
    </label>`;
  }

  // ---- Rendering ----

  protected render() {
    const showPager = this.pageCount > 1;
    const showInfo = this.showPaginationInfo && this.rowCount > 0;
    const showPageSize = this.pageSizeOptions.length > 0;
    const paginator =
      showPager || showInfo || showPageSize
        ? html`<div class="wm-table-paginator-row wm-table-paginator-row--${this.paginatorAlign}">
            <div class="wm-table-pagination-cluster">
              ${showInfo ? html`<div class="wm-table-pagination-info">${this.paginationInfoText()}</div>` : nothing}
              ${showPager
                ? html`<webmodule-table-paginator
                    .index=${this.pageIndex}
                    .count=${this.pageCount}
                    @page-change=${(e: CustomEvent) => (this.pageIndex = e.detail.index as number)}
                  ></webmodule-table-paginator>`
                : nothing}
              ${showPageSize ? this.pageSizeTemplate() : nothing}
            </div>
          </div>`
        : nothing;

    const scrollStyle = styleMap(this.bodyMaxHeight ? { maxHeight: this.bodyMaxHeight } : {});
    const rootStyle = styleMap(this.minTableWidth ? { minWidth: this.minTableWidth } : {});

    return html`
      <div class="wm-table ${this.tablestyle}" style=${rootStyle}>
        <div class="wm-table-scroll" style=${scrollStyle}>
          <div class="wm-table-header">
            <div class="wm-table-header-row">${this.headerTemplate()}</div>
          </div>
          <div class="wm-table-content">${this.bodyTemplate()}</div>
          ${this.hasFooterContent() ? html`<div class="wm-table-footer">${this.footerTemplate()}</div>` : nothing}
        </div>
        ${paginator}
      </div>
    `;
  }

  private isVisible(column: WebModuleTableColumnDef): boolean {
    return column.visible ?? true;
  }

  // Memoised for the duration of a render: rowTemplate/footer call this per row, and
  // the `columns` getter rebuilds the drag-handle column on every access. Cleared in
  // willUpdate so it recomputes once per update.
  private _columnGroupsCache: ColumnGroups | null = null;

  private getVisibleColumnGroups(): ColumnGroups {
    if (this._columnGroupsCache) return this._columnGroupsCache;
    const ordered: ColumnEntry[] = [];
    const left: ColumnEntry[] = [];
    const center: ColumnEntry[] = [];
    const right: ColumnEntry[] = [];
    let visibleIndex = 0;
    this.columns.forEach(column => {
      if (!this.isVisible(column)) return;
      const entry: ColumnEntry = { column, index: visibleIndex };
      ordered.push(entry);
      if (column.fixed === 'left') left.push(entry);
      else if (column.fixed === 'right') right.push(entry);
      else center.push(entry);
      visibleIndex++;
    });
    this._columnGroupsCache = { ordered, left, center, right, total: visibleIndex };
    return this._columnGroupsCache;
  }

  private getCellWidthStyle(colDef: WebModuleTableColumnDef) {
    const styles: Record<string, string> = {};
    if (colDef.width) {
      styles.flex = `0 0 ${colDef.width}`;
      styles.width = colDef.width;
    }
    if (colDef.minWidth) styles.minWidth = colDef.minWidth;
    if (colDef.maxWidth) styles.maxWidth = colDef.maxWidth;
    return styleMap(styles);
  }

  private posClass(index: number, total: number): string {
    return index === 0 ? 'first' : index === total - 1 ? 'last' : '';
  }

  private fixedClass(colDef: WebModuleTableColumnDef): string {
    return colDef.fixed ? ` wm-table-cell--fixed wm-table-cell--fixed-${colDef.fixed}` : '';
  }

  // ---- Header ----

  protected headerTemplate(): unknown {
    const groups = this.getVisibleColumnGroups();
    const total = groups.total;

    const renderSegment = (entries: ColumnEntry[], segment: 'left' | 'center' | 'right') => {
      if (!entries.length) return nothing;
      const content = entries.map(({ column, index }) => this.getColumnHeaderTemplate(column, index, total));
      if (segment === 'center') {
        return html`<div class="wm-table-header-segment wm-table-header-segment-center">
          <div class="wm-table-header-center-track">${content}</div>
        </div>`;
      }
      const cls = segment === 'left' ? 'wm-table-header-segment-left' : 'wm-table-header-segment-right';
      return html`<div class="wm-table-header-segment ${cls}">${content}</div>`;
    };

    return html`
      ${renderSegment(groups.left, 'left')}${renderSegment(groups.center, 'center')}${renderSegment(
        groups.right,
        'right'
      )}
    `;
  }

  protected getColumnHeaderTemplate(defs: WebModuleTableColumnDef, index: number, total: number) {
    const cellStyle = this.getCellWidthStyle(defs);
    const alignClass = defs.align ? `text-${defs.align}` : '';
    const pos = this.posClass(index, total);
    const fixed = this.fixedClass(defs);
    const id = defs.id;

    const sortEvent = defs.sortable
      ? (e: Event) => {
          e.stopImmediatePropagation();
          if (this.sortField === defs.fieldName) {
            this.sortAscending = !this.sortAscending;
          } else {
            this.sortField = defs.fieldName;
            this.sortAscending = defs.isDefaultSortDirectionAscending ?? true;
          }
          this.pageIndex = 0;
        }
      : undefined;

    // Sortable headers are operable by keyboard (Enter/Space), mirroring the click.
    const sortKeyDown =
      sortEvent &&
      ((e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          sortEvent(e);
        }
      });

    // Every sortable column shows the dual up/down indicator (both dimmed). When the
    // column is the active sort, its direction's arrow is highlighted.
    const sortDir =
      defs.sortable && this.sortField === defs.fieldName ? (this.isSortingAscending ? 'asc' : 'desc') : '';
    const sortIcon = defs.sortable
      ? html`<span class="wm-table-sort-icon" data-dir=${sortDir} aria-hidden="true"
          ><span class="wm-table-sort-asc">↑</span><span class="wm-table-sort-desc">↓</span></span
        >`
      : nothing;

    // Each region can be configured in JS (column def) and/or overridden in
    // HTML via the `<id>-*` slot. The JS value is the slot's fallback content,
    // so the slot wins when filled. When the column has no `id`, only the JS
    // value renders (slots require an id to be addressable).
    const region = (slotSuffix: string, jsValue: unknown) =>
      id ? html`<slot name="${id}-${slotSuffix}">${jsValue}</slot>` : (jsValue ?? nothing);

    const labelContent =
      defs.label !== undefined ? this.resolveHeaderContent(defs.label) : awaitableTemplate(defs.title);

    const hasActions = !!id || defs.headerActions !== undefined;
    const trailing =
      defs.sortable || hasActions
        ? html`<div class="wm-table-header-trailing">
            ${sortIcon}
            ${hasActions
              ? html`<div class="wm-table-header-actions" @click=${(e: Event) => e.stopPropagation()}>
                  ${region('header-actions', this.resolveHeaderContent(defs.headerActions))}
                </div>`
              : nothing}
          </div>`
        : nothing;

    return html`<div
      role=${ifDefined(defs.sortable ? 'button' : undefined)}
      tabindex=${ifDefined(defs.sortable ? 0 : undefined)}
      @click=${sortEvent}
      @keydown=${ifDefined(sortKeyDown || undefined)}
      class="wm-table-cell wm-table-header-cell ${defs.classes ?? ''} ${defs.sortable
        ? 'sorting'
        : 'sorting-disabled'} ${pos}${fixed} ${alignClass}"
      style=${cellStyle}
      data-col-index=${index}
    >
      ${region('prefix', this.resolveHeaderContent(defs.headerPrefix))}
      <span class="wm-table-header-label">${region('label', labelContent)}</span>
      ${region('suffix', this.resolveHeaderContent(defs.headerSuffix))} ${trailing}
    </div>`;
  }

  /** Resolves a header-region value (string | template | factory) for rendering. */
  private resolveHeaderContent(value?: string | TemplateResult | (() => string | TemplateResult)): unknown {
    if (value === undefined) return nothing;
    return typeof value === 'function' ? value() : value;
  }

  // ---- Body ----

  protected getPageData(): object[] {
    if (!this.data || this.visibleMode === 'all') return this.data ?? [];
    const keys = this._selectedKeySet;
    if (this.visibleMode === 'selected') return this.data.filter(item => keys.has(this.getPrimaryKey(item)));
    if (this.visibleMode === 'unselected') return this.data.filter(item => !keys.has(this.getPrimaryKey(item)));
    return this.data;
  }

  protected bodyTemplate(): unknown {
    const data = this.getPageData();
    const emptyGroups = this.isGrouped ? (this.emptyGroups ?? []) : [];

    if (!data.length && !emptyGroups.length) {
      const msg = this.emptyMessage;
      return msg ? html`<div class="wm-table-empty">${msg}</div>` : nothing;
    }

    if (!this.isGrouped) {
      return html`${repeat(
        data,
        item => this.getPrimaryKey(item),
        (item, index) => this.rowTemplate(item, index)
      )}`;
    }

    const groups = this.buildGroups(data);
    this._groupsByKey = new Map(groups.map(g => [g.key, g]));

    // Index the empty groups by their anchor row and register each for group-drop
    // value lookups (they are absent from the row-derived _groupsByKey above).
    const emptyByIndex = new Map<number, WebModuleTableEmptyGroup[]>();
    for (const eg of emptyGroups) {
      const list = emptyByIndex.get(eg.beforeIndex);
      if (list) list.push(eg);
      else emptyByIndex.set(eg.beforeIndex, [eg]);
      if (!this._groupsByKey.has(eg.key))
        this._groupsByKey.set(eg.key, { value: eg.value, key: eg.key, items: [], ungrouped: false });
    }
    const hasGroupFooter = this.columns.some(c => typeof c.groupAggregate === 'function' && this.isVisible(c));

    const out: unknown[] = [];
    let rowIndex = 0;
    const flushEmptyGroups = (index: number) => {
      const pending = emptyByIndex.get(index);
      if (!pending) return;
      for (const eg of pending)
        out.push(this.renderGroupHeader({ value: eg.value, key: eg.key, items: [], ungrouped: false }, index, index));
    };

    for (const group of groups) {
      // Ungrouped runs render as plain rows: no header band, no footer, never
      // collapsible. This lets a table group only some of its rows.
      if (group.ungrouped) {
        for (const item of group.items) {
          flushEmptyGroups(rowIndex);
          out.push(this.rowTemplate(item, rowIndex));
          rowIndex++;
        }
        continue;
      }
      flushEmptyGroups(rowIndex);
      const start = rowIndex;
      const end = start + group.items.length;
      out.push(this.renderGroupHeader(group, start, end));
      const collapsed = this.collapsibleGroups && this._collapsedGroups.has(group.key);
      if (!collapsed) {
        for (const item of group.items) {
          out.push(this.rowTemplate(item, rowIndex));
          rowIndex++;
        }
        if (hasGroupFooter) out.push(this.renderGroupFooter(group));
      } else {
        rowIndex += group.items.length;
      }
    }
    // Trailing empty groups (anchored at/after the last row), plus any whose anchor
    // fell past the rendered rows, so nothing is silently dropped.
    flushEmptyGroups(rowIndex);
    for (const [index, pending] of emptyByIndex) {
      if (index <= rowIndex) continue;
      for (const eg of pending)
        out.push(
          this.renderGroupHeader({ value: eg.value, key: eg.key, items: [], ungrouped: false }, rowIndex, rowIndex)
        );
    }
    return html`${out}`;
  }

  private getFieldValue(item: object, fieldName: string): unknown {
    const parts = fieldName.split('.');
    let value: unknown = item;
    for (const part of parts) {
      if (value === null || value === undefined) return undefined;
      value = (value as Record<string, unknown>)[part];
    }
    return value;
  }

  private buildGroups(data: object[]): RowGroup[] {
    const groups: RowGroup[] = [];
    let current: RowGroup | null = null;
    for (const item of data) {
      const value = this.getGroupByKey ? this.getGroupByKey(item) : this.getFieldValue(item, this.groupBy!);
      const ungrouped = value === null || value === undefined;
      const key = ungrouped ? UNGROUPED_KEY : String(value);
      // Ungrouped rows never merge into a header band, so keep each contiguous
      // run its own bucket and never coalesce two ungrouped runs.
      if (current === null || current.key !== key || ungrouped !== current.ungrouped) {
        current = { value, key, items: [], ungrouped };
        groups.push(current);
      }
      current.items.push(item);
    }
    return groups;
  }

  private renderGroupHeader(group: RowGroup, start: number, end: number): TemplateResult {
    const collapsed = this._collapsedGroups.has(group.key);
    const toggle = (e: Event) => {
      e.stopImmediatePropagation();
      if (!this.collapsibleGroups) return;
      const next = new Set(this._collapsedGroups);
      if (next.has(group.key)) next.delete(group.key);
      else next.add(group.key);
      this._collapsedGroups = next;
      this.dispatchCustom('grouptoggle', {
        table: this,
        value: group.value,
        key: group.key,
        collapsed: next.has(group.key)
      });
    };

    const chevron = this.collapsibleGroups
      ? html`<webmodule-icon
          class="wm-table-group-toggle"
          name=${collapsed ? 'chevron-right' : 'chevron-down'}
        ></webmodule-icon>`
      : nothing;

    const content = this.groupHeaderTemplate
      ? this.groupHeaderTemplate(group.value, group.items, this)
      : html`<span class="wm-table-group-header-label"
            >${group.value !== null && group.value !== undefined ? String(group.value) : '(No value)'}</span
          ><span class="wm-table-group-header-count">(${group.items.length})</span>`;

    const dragEnabled = this.enableDrag;

    const action = this.groupActionTemplate ? this.groupActionTemplate(group.value, group.items, this) : null;

    return html`<div
      class="wm-table-row wm-table-group-header-row"
      data-group-key=${group.key}
      data-group-start=${start}
      data-group-end=${end}
      @dragover=${dragEnabled ? this.handleGroupDragOver : undefined}
      @dragleave=${dragEnabled ? this.handleGroupDragLeave : undefined}
      @drop=${dragEnabled ? this.handleGroupDrop : undefined}
    >
      <div
        class="wm-table-group-header-cell ${this.collapsibleGroups ? 'collapsible' : ''}"
        role=${ifDefined(this.collapsibleGroups ? 'button' : undefined)}
        tabindex=${ifDefined(this.collapsibleGroups ? 0 : undefined)}
        aria-expanded=${ifDefined(this.collapsibleGroups ? String(!collapsed) : undefined)}
        @click=${this.collapsibleGroups ? toggle : undefined}
        @keydown=${this.collapsibleGroups
          ? (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggle(e);
              }
            }
          : undefined}
      >
        ${chevron}${content}
      </div>
      ${action !== null ? html`<div class="wm-table-group-header-action">${action}</div>` : nothing}
    </div>`;
  }

  private renderGroupFooter(group: RowGroup): TemplateResult {
    const groups = this.getVisibleColumnGroups();
    const total = groups.total;

    const cell = ({ column, index }: ColumnEntry) => {
      const value = typeof column.groupAggregate === 'function' ? column.groupAggregate(group.items, group.value) : '';
      return html`<div
        class="wm-table-cell ${column.classes ?? ''} ${this.posClass(index, total)}${this.fixedClass(
          column
        )} ${column.align ? `text-${column.align}` : ''}"
        style=${this.getCellWidthStyle(column)}
        data-col-index=${index}
      >
        ${value}
      </div>`;
    };

    return html`<div class="wm-table-row wm-table-group-footer-row">${this.renderSegments(groups, cell)}</div>`;
  }

  /** Renders the three column segments (left/center/right) for a row body. */
  private renderSegments(groups: ColumnGroups, cell: (entry: ColumnEntry) => TemplateResult): TemplateResult {
    const left = groups.left.map(cell);
    const center = groups.center.map(cell);
    const right = groups.right.map(cell);
    return html`
      ${groups.left.length ? html`<div class="wm-table-row-segment wm-table-row-segment-left">${left}</div>` : nothing}
      ${groups.center.length
        ? html`<div class="wm-table-row-segment wm-table-row-segment-center">
            <div class="wm-table-row-center-track">${center}</div>
          </div>`
        : nothing}
      ${groups.right.length
        ? html`<div class="wm-table-row-segment wm-table-row-segment-right">${right}</div>`
        : nothing}
    `;
  }

  /**
   * Wraps a cell's content in `<webmodule-fade>` when the column opts in via
   * `fade` (and the table-wide {@link disableCellFade} is off). Returns the
   * content unchanged otherwise, so non-fading columns render exactly as before.
   */
  protected wrapCellFade(colDef: WebModuleTableColumnDef, content: unknown): unknown {
    if (this.disableCellFade || !colDef.fade) {
      return content;
    }

    const opts: WebModuleTableCellFade = colDef.fade === true ? {} : colDef.fade;
    return html`<webmodule-fade
      class="wm-table-cell-fade"
      orientation=${opts.orientation ?? 'horizontal'}
      ?without-shadow=${opts.withoutShadow ?? false}
      ?without-scrollbar=${opts.withoutScrollbar ?? true}
      ?without-wheel=${opts.withoutWheel ?? true}
      >${content}</webmodule-fade
    >`;
  }

  protected columnTemplate(
    item: object,
    itemIndex: number,
    colDef: WebModuleTableColumnDef,
    colindex: number,
    columnCount: number
  ): TemplateResult {
    const clickEvent = colDef.click
      ? (e: Event) => {
          if (!colDef.click?.(e, this, item, colDef.fieldName)) this.cellClickEvent(e, item, colDef);
          else e.stopImmediatePropagation();
        }
      : (e: Event) => this.cellClickEvent(e, item, colDef);

    const displayVal =
      colDef.displayValue?.(this, item, itemIndex, colDef.fieldName) ??
      (item as Record<string, unknown>)[colDef.fieldName] ??
      '';

    const pos = this.posClass(colindex, columnCount);
    const fixed = this.fixedClass(colDef);
    const alignClass = colDef.align ? `text-${colDef.align}` : '';
    const classes = `wm-table-cell ${colDef.classes ?? ''} ${pos}${fixed} ${alignClass}`;

    return html`<div
      role=${ifDefined(colDef.click ? 'button' : undefined)}
      @click=${clickEvent}
      class=${classes}
      style=${this.getCellWidthStyle(colDef)}
      data-col-index=${colindex}
    >
      ${this.wrapCellFade(colDef, displayVal)}
    </div>`;
  }

  public rowTemplate(item: object, index: number): unknown {
    const groups = this.getVisibleColumnGroups();
    const columnCount = groups.total;
    const cell = ({ column, index: colIndex }: ColumnEntry) =>
      this.columnTemplate(item, index, column, colIndex, columnCount);

    const itemRowClasses = this.getItemRowClass(item);
    const variant = this.rowVariantEvent?.(item);
    const variantClass = variant ? `wm-table-row--${variant}` : '';
    const itemKey = this.getPrimaryKey(item);
    const isSelected = this._selectedKeySet.has(itemKey);
    const enableDragDrop = this.enableDragOnRow(item);
    const noDrop = this.enableDrag && !enableDragDrop ? this.noDropClass : '';
    const clickable = this.clickrows || this.isSelectable;

    const rowClick = (e: Event) => {
      e.stopImmediatePropagation();
      this.rowClicked(item);
    };
    const rowKeyDown = (e: KeyboardEvent) => this.handleRowKeyDown(e, item);

    const extension = this.extensionTemplate(item);
    const beforeRowTemplate = this.beforeRowTemplate(item, index);
    const afterRowTemplate = this.afterRowTemplate(item, index);

    return html`${beforeRowTemplate}
      <div
        @click=${clickable ? rowClick : undefined}
        @keydown=${clickable ? rowKeyDown : undefined}
        role=${ifDefined(clickable ? 'button' : undefined)}
        tabindex=${ifDefined(clickable ? 0 : undefined)}
        aria-selected=${ifDefined(this.isSelectable ? (isSelected ? 'true' : 'false') : undefined)}
        class="wm-table-row ${itemRowClasses} ${variantClass} ${isSelected ? 'selected' : ''} ${noDrop}"
        @dragover=${this.handleDragOver}
        @dragleave=${this.handleDragLeave}
        @drop=${this.handleDrop}
        data-index=${index}
        data-rowid=${this.getRowId(item) ?? nothing}
      >
        ${this.renderSegments(groups, cell)}
      </div>
      ${extension ?? ''}${afterRowTemplate}`;
  }

  /**
   * Renders the given items as column-aligned rows using this table's columns.
   * Useful inside an expander to show child rows that line up with the parent:
   * `() => ({ template: table.renderChildRows(children), layout: 'columns' })`.
   */
  public renderChildRows(items: object[]): TemplateResult {
    const groups = this.getVisibleColumnGroups();
    const columnCount = groups.total;
    const rows = items.map((item, i) => {
      const cell = ({ column, index: colIndex }: ColumnEntry) =>
        this.columnTemplate(item, i, column, colIndex, columnCount);
      return html`<div class="wm-table-row">${this.renderSegments(groups, cell)}</div>`;
    });
    return html`${rows}`;
  }

  private handleRowKeyDown(event: KeyboardEvent, item: object) {
    if (!this.clickrows && !this.isSelectable) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.rowClicked(item);
  }

  private extensionTemplate(item: object): TemplateResult | undefined {
    const promise = this.createExtensionTemplate(item);
    if (!promise) return undefined;
    return html`${until(
      promise.then(result => {
        const layoutClass = result.layout === 'columns' ? ' wm-table-row-extension--columns' : '';
        return html`<div class="wm-table-row-extension${layoutClass}">${result.template}</div>`;
      }),
      nothing
    )}`;
  }

  protected createExtensionTemplate(item: object): Promise<WebModuleTableExtensionResult> | null {
    const itemKey = this.getPrimaryKey(item);
    const provider = this._extensionByKey.get(itemKey)?.extensionProvider;
    if (!provider) return null;

    const normalize = (result: TemplateResult | WebModuleTableExtensionResult): WebModuleTableExtensionResult =>
      result && typeof result === 'object' && 'template' in result ? result : { template: result };

    const value = typeof provider === 'function' ? provider(item) : provider;
    return value instanceof Promise ? value.then(normalize) : Promise.resolve(normalize(value));
  }

  protected rowClicked(item: object) {
    if (this.isSelectable) {
      const itemKey = this.getPrimaryKey(item);
      if (this.selectmode === 'single') {
        this.selectedKeys = [itemKey];
      } else if (this.selectmode === 'multi') {
        const keyIndex = this.selectedKeys.indexOf(itemKey);
        if (keyIndex === -1) this.selectedKeys = [...this.selectedKeys.filter(x => x !== itemKey), itemKey];
        else this.selectedKeys = this.selectedKeys.filter(x => x !== itemKey);
      }
      this.rowSelectEvent();
    }
    if (this.clickrows) this.rowClickEvent(item);
  }

  // ---- Footer (grand totals) ----

  /** True when any visible column contributes a grand-total or sub-total cell. */
  protected hasFooterContent(): boolean {
    return this.columns.some(
      c => this.isVisible(c) && (typeof c.columnAggregate === 'function' || typeof c.columnSubAggregate === 'function')
    );
  }

  protected footerTemplate(): unknown {
    const hasAggregate = this.columns.some(c => typeof c.columnAggregate === 'function' && this.isVisible(c));

    const groups = this.getVisibleColumnGroups();
    const total = groups.total;
    const cell = ({ column, index }: ColumnEntry) => {
      const value = typeof column.columnAggregate === 'function' ? column.columnAggregate() : '';
      return html`<div
        class="wm-table-cell ${column.classes ?? ''} ${this.posClass(index, total)}${this.fixedClass(
          column
        )} ${column.align ? `text-${column.align}` : ''}"
        style=${this.getCellWidthStyle(column)}
        data-col-index=${index}
      >
        ${value}
      </div>`;
    };

    // The grand-total row renders only when a column defines columnAggregate, but the
    // sub-total row must still render for a sub-aggregate-only table.
    return html`${hasAggregate ? html`<div class="wm-table-row">${this.renderSegments(groups, cell)}</div>` : nothing}
    ${this.subFooterTemplate(groups)}`;
  }

  protected subFooterTemplate(groups: ColumnGroups): unknown {
    const hasSub = this.columns.some(c => typeof c.columnSubAggregate === 'function' && this.isVisible(c));
    if (!hasSub) return nothing;
    const total = groups.total;
    const cell = ({ column, index }: ColumnEntry) => {
      const value = typeof column.columnSubAggregate === 'function' ? column.columnSubAggregate() : '';
      return html`<div
        class="wm-table-cell ${column.classes ?? ''} ${this.posClass(index, total)}${this.fixedClass(
          column
        )} ${column.align ? `text-${column.align}` : ''}"
        style=${this.getCellWidthStyle(column)}
        data-col-index=${index}
      >
        ${value}
      </div>`;
    };
    return html`<div class="wm-table-row wm-table-sub-footer-row">${this.renderSegments(groups, cell)}</div>`;
  }

  // ---- Drag & drop ----

  private handleTouchCancel() {
    this.cleanupDragEnd();
  }

  private dragtouchstart = (event: TouchEvent) => {
    // Only the grip handle carries the touch listeners, and it is only rendered
    // for drag-enabled rows, so reaching here means this row is draggable.
    const row = (event.target as HTMLElement).closest<HTMLElement>('.wm-table-row');
    if (!row) return;
    row.classList.add('ghost-row');
    this.dragSrcEl = row;
    this.draggedIndex = Number(row.dataset.index);
  };

  private dragtouchmove = (event: TouchEvent) => {
    event.preventDefault();
    const touch = event.touches[0];
    const targetRow = document.elementFromPoint(touch.clientX, touch.clientY)?.closest<HTMLElement>('.wm-table-row');
    if (!targetRow) {
      this.lastDropTarget?.classList.remove('drop-target-at');
      this.lastDropTarget = null;
      return;
    }
    if (targetRow !== this.dragSrcEl && targetRow !== this.lastDropTarget) {
      this.lastDropTarget?.classList.remove('drop-target-at');
      targetRow.classList.add('drop-target-at');
      this.lastDropTarget = targetRow;
    }
  };

  private dragtouchend = () => {
    if (this.lastDropTarget) this.dropHandle(this.lastDropTarget);
    this.cleanupDragEnd();
  };

  private handleDragStart(e: DragEvent): void {
    // Drag is initiated from the grip handle; resolve the owning row.
    const row = (e.currentTarget as HTMLElement).closest<HTMLElement>('.wm-table-row');
    if (!this.enableDrag || !row) {
      e.preventDefault();
      return;
    }
    this.dragSrcEl = row;
    this.draggedIndex = Number(row.dataset.index);
    row.classList.add('ghost-row');
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      // Required by some browsers (notably Firefox): a drag with no data set is
      // treated as undroppable, so `dragover` fires (showing the indicator) but
      // `drop` never does and nothing moves.
      e.dataTransfer.setData('text/plain', row.dataset.index ?? '');
      e.dataTransfer.setDragImage(row, 0, 0);
    }
  }

  private handleDragOver(e: DragEvent): void {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;
    if (!this.enableDrag) {
      e.stopPropagation();
      return;
    }
    // Non-draggable rows are not valid drop targets: show the "no drop" cursor
    // and skip the indicator so they read as locked.
    if (target.classList.contains(this.noDropClass)) {
      e.dataTransfer!.dropEffect = 'none';
      return;
    }
    e.dataTransfer!.dropEffect = 'move';
    if (target !== this.dragSrcEl) target.classList.add('drop-target-at');
  }

  private handleDragLeave(e: DragEvent): void {
    (e.currentTarget as HTMLElement).classList.remove('drop-target-at');
  }

  private handleDrop(e: DragEvent) {
    e.preventDefault();
    this.dropHandle(e.currentTarget as HTMLElement);
  }

  private handleGroupDragOver(e: DragEvent): void {
    if (!this.enableDrag || this.draggedIndex === null) return;
    e.preventDefault();
    e.dataTransfer!.dropEffect = 'move';
    (e.currentTarget as HTMLElement).classList.add('drop-target-group');
  }

  private handleGroupDragLeave(e: DragEvent): void {
    (e.currentTarget as HTMLElement).classList.remove('drop-target-group');
  }

  private handleGroupDrop(e: DragEvent): void {
    e.preventDefault();
    (e.currentTarget as HTMLElement).classList.remove('drop-target-group');
    this.dropHandle(e.currentTarget as HTMLElement);
  }

  private dropHandle(target: HTMLElement) {
    if (!this.enableDrag || this.draggedIndex === null) return;
    if (target.classList.contains('wm-table-group-header-row')) {
      this.groupDropHandle(target);
      return;
    }
    if (target === this.dragSrcEl || target.classList.contains(this.noDropClass)) return;
    const insertAt = Number(target.dataset.index);
    if (Number.isNaN(insertAt)) return;
    const move: WebModuleTableItemMove = { sourceIndex: this.draggedIndex, targetIndex: insertAt };
    this.dispatchCustom('webmodule-table-item-move', move);
  }

  private groupDropHandle(header: HTMLElement) {
    if (this.draggedIndex === null) return;
    const key = header.dataset.groupKey;
    const start = Number(header.dataset.groupStart);
    const end = Number(header.dataset.groupEnd);
    if (Number.isNaN(start) || Number.isNaN(end)) return;
    const position = this.groupDropPosition;
    const move: WebModuleTableItemMove = {
      sourceIndex: this.draggedIndex,
      targetIndex: position === 'start' ? start : end,
      targetGroupKey: key,
      targetGroupValue: key !== undefined ? this._groupsByKey.get(key)?.value : undefined,
      dropPosition: position
    };
    this.dispatchCustom('webmodule-table-item-move', move);
  }

  private handleDragEnd(e: DragEvent): void {
    e.preventDefault();
    this.cleanupDragEnd();
  }

  private cleanupDragEnd() {
    this.renderRoot.querySelectorAll('.wm-table-row').forEach(row => {
      row.classList.remove('ghost-row');
      row.classList.remove('drop-target-at');
      row.classList.remove('drop-target-group');
    });
    this.dragSrcEl = null;
    this.draggedIndex = null;
  }

  private dispatchCustom(name: string, detail: object) {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: false, composed: true }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'webmodule-table-paginator': WebModuleTablePaginator;
    'webmodule-table': WebModuleTable;
  }
}
